import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CollapseState } from '@recruitee/common';

import { HotkeyPressEvent, HotkeysService } from '../../core/a11y/hotkeys.service';
import { KeyCode } from '../../core/keycode';
import { DialogService } from '../../dialogs/dialogs.service';
import { SidebarInternalService } from './sidebar-internal.service';

export const collapseHorizontal = trigger('collapseHorizontal', [
  state(
    'collapsed',
    style({
      width: '56px',
      minWidth: '56px',
    }),
  ),
  state(
    'normal',
    style({
      width: '*',
      minWidth: '*',
    }),
  ),
  // immediate change state from none state to collapsed
  transition('void <=> collapsed', animate('0ms')),
  transition('* <=> collapsed', animate('200ms ease-in-out')),
  transition('collapsed <=> normal', animate('200ms ease-in-out')),
]);

@Component({
  selector: 'rt-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.less'],
  providers: [SidebarInternalService],
  animations: [collapseHorizontal],
})
export class SidebarComponent implements OnInit {
  @Input() public hasExpander: boolean = true;
  @Input() public isExpanded: boolean = true;
  @Input() public size: 'default' | 'large' = 'default';

  @Output() public expanded: EventEmitter<boolean> = new EventEmitter();
  @Output() public animationEvent: EventEmitter<AnimationEvent> = new EventEmitter();

  public isMinimized: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private sidebarInternalService: SidebarInternalService,
    private hotkeysService: HotkeysService,
    private dialogService: DialogService,
  ) {}

  public ngOnInit(): void {
    this.isMinimized = !this.isExpanded;
    this.sidebarInternalService.sidebarMinimized.next(this.isMinimized);

    if (this.hasExpander) {
      merge(
        this.hotkeysService.shortcut([KeyCode.BracketLeft], true, false),
        this.hotkeysService.shortcut([KeyCode.BracketRight], true, false),
      )
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe((event: HotkeyPressEvent) => {
          this.handleHotkeyEvent(event);
        });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isExpanded?.currentValue) {
      this.isMinimized = !this.isExpanded;
      this.sidebarInternalService.sidebarMinimized.next(this.isMinimized);
    }
  }

  public get collapseState(): CollapseState {
    return this.isMinimized ? CollapseState.Collapsed : CollapseState.Normal;
  }

  public toggleSidebar(isMinimized: boolean): void {
    this.isMinimized = isMinimized;
    this.sidebarInternalService.sidebarMinimized.next(isMinimized);
    this.expanded.emit(!isMinimized);
    this.cdRef.detectChanges();
  }

  public handleHotkeyEvent(event: HotkeyPressEvent): void {
    if (this.dialogService.hasDialogsOpen()) return;

    switch (event.keys[0]) {
      case 'BracketRight':
        this.toggleSidebar(false);
        break;
      case 'BracketLeft':
        this.toggleSidebar(true);
        break;
      default:
        return;
    }
  }
}
